// src/components/billing/QuotaProgressBar.tsx
/**
 * A progress bar component for displaying quota usage.
 * Shows used/limit with percentage and warning states.
 */

import React from "react";
import {
  Box,
  LinearProgress,
  Typography,
  Tooltip,
  useTheme,
} from "@mui/material";
import { QuotaStatus } from "../../hooks/useBilling";
import { glass } from "../../theme/glassTokens";

interface QuotaProgressBarProps {
  quota: QuotaStatus;
  label?: string;
  showDetails?: boolean;
  height?: number;
}

const QuotaProgressBar: React.FC<QuotaProgressBarProps> = ({
  quota,
  label,
  showDetails = true,
  height = 10,
}) => {
  const theme = useTheme();

  // Determine color based on usage
  const getColor = () => {
    if (quota.is_unlimited) return theme.palette.success.light;
    if (quota.is_exceeded) return theme.palette.error.light;
    if (quota.percentage_used >= 90) return theme.palette.warning.main;
    if (quota.percentage_used >= 75) return theme.palette.warning.light;
    return glass.accent;
  };

  // Format numbers with commas
  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  // Get status text
  const getStatusText = () => {
    if (quota.is_unlimited) return "Unlimited";
    if (quota.is_exceeded) return "Quota exceeded";
    return `${formatNumber(quota.remaining)} remaining`;
  };

  const color = getColor();
  const percentage = quota.is_unlimited ? 0 : Math.min(quota.percentage_used, 100);

  return (
    <Box sx={{ width: "100%" }}>
      {(label || showDetails) && (
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          {label && (
            <Typography variant="body2" sx={{ color: glass.textSecondary }}>
              {label}
            </Typography>
          )}
          {showDetails && (
            <Typography variant="body2" sx={{ color: glass.textSecondary }}>
              {quota.is_unlimited ? (
                "Unlimited"
              ) : (
                <>
                  <strong>{formatNumber(quota.used)}</strong> / {formatNumber(quota.limit)}
                </>
              )}
            </Typography>
          )}
        </Box>
      )}
      
      <Tooltip title={getStatusText()} arrow placement="top">
        <Box sx={{ position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={percentage}
            sx={{
              height,
              borderRadius: height / 2,
              backgroundColor: theme.palette.action.hover,
              "& .MuiLinearProgress-bar": {
                borderRadius: height / 2,
                backgroundColor: color,
              },
            }}
          />
          {quota.is_unlimited && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: height * 0.7,
                  color: glass.textSecondary,
                }}
              >
                ∞
              </Typography>
            </Box>
          )}
        </Box>
      </Tooltip>
      
      {showDetails && !quota.is_unlimited && (
        <Typography
          variant="caption"
          sx={{
            color: quota.is_exceeded ? theme.palette.error.light : glass.textSecondary,
            mt: 0.25,
            display: "block",
          }}
        >
          {getStatusText()}
        </Typography>
      )}
    </Box>
  );
};

export default QuotaProgressBar;
